import type { Experiment } from 'types';
import { useState } from 'react';
import { Edit2 } from 'lucide-react';
import { useForm } from 'hooks/useForm';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useExperiment } from '@/state/experiments/useExperiment';
import { SectionAuthor } from '../SectionAuthor';
import { HelpTip } from '../ExperimentSettings/TargetingHelp';

/** The card's words, for a page other than an experiment's Brief (the feature flag page's). */
export type BriefCardCopy = { title: string; description: string; help?: string };

const EXPERIMENT_COPY: BriefCardCopy = {
  title: 'Description & notes',
  description: 'Add detailed information and any relevant notes about your experiment.',
};

type DescriptionProps = { experiment: Experiment; copy?: BriefCardCopy };

export const Description = ({ experiment, copy = EXPERIMENT_COPY }: DescriptionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    formRef,
    state: formState,
    update,
  } = useForm<Partial<Experiment>>({
    description: experiment.description,
  });
  const { updateExperiment } = useExperiment(experiment.id);

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    await updateExperiment(experiment.id, formState);
    setIsEditing(false);
  };

  const onCancel = () => {
    setIsEditing(false);
    update({ description: experiment.description });
  };

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="brief-description">
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="micro-label text-foreground">{copy.title}</h2>
            {copy.help && <HelpTip text={copy.help} label={copy.title.toLowerCase()} />}
          </div>
          <p className="supporting mt-1">{copy.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SectionAuthor experiment={experiment} section="description" hasContent={!!experiment.description?.trim()} />
          {!isEditing && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Edit description"
              onClick={() => setIsEditing(true)}
              className="text-dim hover:text-foreground hover:bg-hover size-8 shrink-0 rounded-lg"
            >
              <Edit2 className="size-3.5" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      <div className="p-4">
        {isEditing ? (
          <form ref={formRef} onSubmit={onSubmit} className="flex flex-col gap-3">
            <Textarea
              name="description"
              defaultValue={experiment.description || ''}
              rows={6}
              placeholder="Write a description or notes..."
              className="border-line bg-field text-foreground rounded-lg text-body"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white">
                Save
              </Button>
            </div>
          </form>
        ) : (
          <div className="min-h-[100px]">
            {experiment.description ? (
              <p className="text-foreground text-body whitespace-pre-wrap">{experiment.description}</p>
            ) : (
              <p className="supporting">No description added yet.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
